import * as React from "react";

import { Tabs as TabsPrimitive } from "radix-ui";

import { cn } from "@/utils/cn";

const TabsValueContext = React.createContext<string | undefined>(undefined);

function Tabs({
  value: valueProp,
  defaultValue,
  onValueChange,
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  const [internalValue, setInternalValue] = React.useState(defaultValue);
  const isControlled = valueProp !== undefined;
  const value = valueProp ?? internalValue;

  const handleValueChange = (next: string) => {
    // When the value is controlled, `internalValue` is never read again, so
    // setting it only buys an extra render of every panel on each tab change.
    if (!isControlled) setInternalValue(next);
    onValueChange?.(next);
  };

  return (
    <TabsValueContext.Provider value={value}>
      <TabsPrimitive.Root
        data-slot="tabs"
        value={valueProp}
        defaultValue={defaultValue}
        onValueChange={handleValueChange}
        // gap, not space-y: Radix keeps inactive tab panels in the DOM as
        // hidden divs, and Tailwind v4's space-y (margin-bottom on
        // :not(:last-child)) gives the active panel a phantom trailing margin
        // when a hidden panel comes after it. Flex gap skips hidden children.
        className={cn("flex flex-col gap-3", className)}
        {...props}
      />
    </TabsValueContext.Provider>
  );
}

function TabsList({ className, children, ...props }: React.ComponentProps<typeof TabsPrimitive.List>) {
  const value = React.useContext(TabsValueContext);
  const listRef = React.useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = React.useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);

  React.useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;

    const measure = () => {
      const active = list.querySelector<HTMLElement>('[data-slot="tabs-trigger"][data-state="active"]');
      if (!active) return;
      setIndicator({
        left: active.offsetLeft,
        top: active.offsetTop,
        width: active.offsetWidth,
        height: active.offsetHeight,
      });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    return () => observer.disconnect();
  }, [value]);

  return (
    <TabsPrimitive.List
      ref={listRef}
      data-slot="tabs-list"
      className={cn(
        "relative no-scrollbar flex w-full items-center gap-1 overflow-x-auto rounded-lg bg-secondary p-1 shadow-xs inset-shadow-inner-glow-xs",
        className,
      )}
      {...props}
    >
      {indicator && (
        <span
          aria-hidden
          className="pointer-events-none absolute rounded-md bg-primary/10 transition-all duration-150 ease-out"
          style={{
            left: indicator.left,
            top: indicator.top,
            width: indicator.width,
            height: indicator.height,
          }}
        />
      )}
      {children}
    </TabsPrimitive.List>
  );
}

function TabsTrigger({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        "relative z-10 inline-flex shrink-0 items-center justify-center rounded-md px-1.5 py-1.5 text-sm font-medium whitespace-nowrap text-muted-foreground capitalize transition-colors outline-none focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 **:data-[slot=badge]:text-card data-[state=active]:text-primary data-[state=inactive]:**:data-[slot=badge]:bg-muted-foreground",
        className,
      )}
      {...props}
    />
  );
}

function TabsContent({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return <TabsPrimitive.Content data-slot="tabs-content" className={cn("outline-none", className)} {...props} />;
}

export { Tabs, TabsContent, TabsList, TabsTrigger };
